import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../stores/store";
import styles from "./pay-as-you-go-info-page.module.scss";
import { Money } from "../../../../../../model/common/money";
import { useRejectSolarRecommendationOfferMutation } from "../../../../../../service/solar-recommendation-service";
import CustomerPageLayout from "../../../../customer-page-layout/CustomerPageLayout";
import { CustomerLoader } from "../../../../components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

type Props = {
  onContinue: () => void;
};

export const PayAsYouGoInfoPage: React.FC<Props> = ({ onContinue }) => {
  const [rejectOffer, { isLoading }] = useRejectSolarRecommendationOfferMutation();

  const navigate = useNavigate();

  const selectedProduct = useSelector((state: RootState) => state.getSolar.selectedProduct);
  const recommendationDetails = useSelector((state: RootState) => state.getSolar.recommendationDetails);
  const [consentChecked, setConsentChecked] = useState(false);

  if (!selectedProduct) return null;

  const firstImage = selectedProduct.images?.[0]?.imageUrl;
  const price = selectedProduct.priceBreakdown.price;
  const priceDisplay = new Money(price.currency, price.amount).toLocaleString();

  const costPerKwh = selectedProduct.payAsYouGoDetails?.costPerKwh;
  const tariffAmountDisplay = costPerKwh
    ? `${new Money(costPerKwh.currency, costPerKwh.amount).toLocaleString()}/Kwh`
    : `${new Money('', 0).toLocaleString()}/Kwh`;

  const isInStock = selectedProduct.quantityAvailableForSale > 0;

  const getDurationRange = (): string | null => {
    const options = selectedProduct.priceBreakdown.monthlyPaymentOptions;
    if (!options?.length) return null;
    const sorted = [...options].sort((a, b) => a.durationInMonths - b.durationInMonths);
    const first = sorted[0].durationInMonths;
    const lastPart = sorted.length > 1 ? ` — ${sorted[sorted.length - 1].durationInMonths}` : '';
    return `${first}${lastPart} Months Plan`;
  };

  const durationRange = getDurationRange();

  const onAccept = () => {
    onContinue();
  };

  const onReject = async () => {
    if (recommendationDetails) {
      try{
        await rejectOffer(recommendationDetails.id).unwrap();
        toast.success("You have successfully rejected the Pay as you go offer.");
        navigate("/dashboard");
      } catch (error: any) {
        toast.error(error?.data?.title || "Failed to reject the offer. Please try again later.");
      }
    }
  };

  if (isLoading) {
    return (
      <CustomerPageLayout>
        <CustomerLoader
          title="Submitting rejection"
          subtitle="Please wait while we process your request..."
        />
      </CustomerPageLayout>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Left: Product Card */}
      <div className={styles.productCard}>
        <div className={styles.imageContainer}>
          {firstImage ? (
            <img src={firstImage} alt={selectedProduct.productName} />
          ) : (
            <div className={styles.imagePlaceholder}>No Image</div>
          )}
        </div>

        <div className={styles.badgeRow}>
          {isInStock && <span className={styles.inStockBadge}>In Stock</span>}
        </div>

        <div className={styles.productName}>{selectedProduct.productName}</div>

        <div className={styles.packageCostRow}>
          <span className={styles.packageCostLabel}>Package Cost</span>
          <span className={styles.packageCostValue}>{priceDisplay}</span>
        </div>

        <div className={styles.specsRow}>
          {selectedProduct.activeDuration != null && (
            <>
              <span>Up to {selectedProduct.activeDuration} Hours</span>
              <span className={styles.specDivider}>|</span>
            </>
          )}
          <span>{selectedProduct.productCapacity}KVA</span>
          {durationRange && (
            <>
              <span className={styles.specDivider}>|</span>
              <span>{durationRange}</span>
            </>
          )}
        </div>
      </div>

      {/* Right: Payment Information */}
      <div className={styles.paymentInfo}>
        <div className={styles.paymentInfoHeader}>
          <h2 className={styles.paymentInfoTitle}>Payment Information</h2>
          <p className={styles.paymentInfoSubtitle}>
            Your Pay as you go request has been approved, Please read the details below and accept the offer
          </p>
        </div>

        <div className={styles.infoRows}>
          <div className={styles.infoRow}>
            <span className={styles.infoRowLabel}>Tariff Plan</span>
            <span className={styles.infoBadge}>Monthly</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoRowLabel}>Tariff Amount</span>
            <span className={styles.infoBadge}>{tariffAmountDisplay}</span>
          </div>
        </div>

        <div className={styles.consentRow}>
          <div
            className={`${styles.checkbox} ${consentChecked ? styles.checkboxChecked : ''}`}
            onClick={() => setConsentChecked(v => !v)}
            role="checkbox"
            aria-checked={consentChecked}
          />
          <p className={styles.consentText}>
            By Clicking this box, you consent to the EKEDP PowerApp team to access your financial information to
            evaluate your credit worthiness in accordance with our terms and conditions.
          </p>
        </div>

        <div className={styles.actionRow}>
          <button className={styles.rejectButton} onClick={onReject}>
            Reject Offer
          </button>
          <button
            className={styles.acceptButton}
            onClick={onAccept}
            disabled={!consentChecked}
          >
            Accept Offer
          </button>
        </div>
      </div>
    </div>
  );
};
